import { useState, useEffect } from 'react';
import axios from 'axios';
import { Activity, TrendingUp, Zap, Brain } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MLPerformance = ({ token }) => {
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPerformance();
  }, []);

  const fetchPerformance = async () => {
    try {
      const response = await axios.get(`${API}/ml/performance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPerformance(response.data);
    } catch (error) {
      console.error('Failed to fetch ML performance:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-500">Loading ML performance data...</div>
      </div>
    );
  }

  if (!performance) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-500">No ML performance data available</div>
      </div>
    );
  }

  const metrics = [
    {
      label: 'Total Records Processed',
      value: performance.total_records.toLocaleString(),
      icon: Database,
      color: 'text-[#2563EB]',
      bg: 'bg-blue-50'
    },
    {
      label: 'Cleansing Confidence',
      value: `${performance.avg_cleansing_confidence.toFixed(1)}%`,
      icon: Zap,
      color: 'text-[#10B981]',
      bg: 'bg-green-50'
    },
    {
      label: 'Classification Confidence',
      value: `${performance.avg_classification_confidence.toFixed(1)}%`,
      icon: Brain,
      color: 'text-[#7C3AED]',
      bg: 'bg-purple-50'
    },
    {
      label: 'Model Version',
      value: performance.model_version,
      icon: Activity,
      color: 'text-[#F59E0B]',
      bg: 'bg-orange-50'
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-heading font-bold text-[#0F172A] mb-2">ML Performance</h1>
        <p className="text-slate-600">Monitor AI model performance and accuracy</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <div
              key={index}
              data-testid={`ml-metric-${metric.label.toLowerCase().replace(/\s+/g, '-')}`}
              className="bg-white border border-slate-200 rounded-sm p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`${metric.bg} p-3 rounded-sm`}>
                  <Icon className={metric.color} size={24} />
                </div>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">{metric.label}</p>
                <p className="text-2xl font-heading font-bold text-[#0F172A]">{metric.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-lg font-heading font-semibold text-[#0F172A] mb-4">Processing Pipeline</h2>
          <div className="space-y-4">
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Data Cleansing</span>
                <span className="text-sm font-mono font-semibold text-[#10B981]">
                  {performance.cleansed_records} / {performance.total_records}
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div
                  className="bg-[#10B981] h-2 rounded-full transition-all duration-500"
                  style={{ width: `${(performance.cleansed_records / performance.total_records) * 100}%` }}
                ></div>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {((performance.cleansed_records / performance.total_records) * 100).toFixed(1)}% complete
              </p>
            </div>

            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Spend Classification</span>
                <span className="text-sm font-mono font-semibold text-[#2563EB]">
                  {performance.classified_records} / {performance.total_records}
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div
                  className="bg-[#2563EB] h-2 rounded-full transition-all duration-500"
                  style={{ width: `${(performance.classified_records / performance.total_records) * 100}%` }}
                ></div>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {((performance.classified_records / performance.total_records) * 100).toFixed(1)}% complete
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-lg font-heading font-semibold text-[#0F172A] mb-4">Model Information</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-600">Model Type</span>
              <span className="text-sm font-medium text-slate-900">GPT-5.2 (OpenAI)</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-600">Model Version</span>
              <span className="text-sm font-mono font-medium text-slate-900">{performance.model_version}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-600">Last Updated</span>
              <span className="text-sm font-medium text-slate-900">
                {new Date(performance.last_trained).toLocaleDateString()}
              </span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-sm text-slate-600">Status</span>
              <span className="inline-block px-2 py-1 bg-green-100 text-green-700 rounded-sm text-xs font-medium">
                Active
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-sm p-6">
        <h3 className="text-sm font-semibold text-[#0F172A] mb-2">Continuous Learning</h3>
        <p className="text-sm text-slate-600">
          The AI model continuously learns from user corrections and feedback to improve classification accuracy over time.
          Current average confidence: <span className="font-semibold text-[#2563EB]">{performance.avg_classification_confidence.toFixed(1)}%</span>
        </p>
      </div>
    </div>
  );
};

const Database = ({ size = 24, className = '' }) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path>
    </svg>
  );
};